import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import NotificationsNoneRounded from '@mui/icons-material/NotificationsNoneRounded';
import NotificationsRounded from '@mui/icons-material/NotificationsRounded';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import DoneAllRounded from '@mui/icons-material/DoneAllRounded';
import { workNotifications } from '../api/workNotifications';
import { notify } from '../notify';
import { colorFor } from './work/workUtil';

const POLL_MS = 45000;

/** What each type means to a person deciding whether to mute it. */
const TYPE_LABELS = {
  ASSIGNED: 'Assigned to me',
  UNASSIGNED: 'Unassigned from me',
  MENTIONED: '@mentions',
  COMMENTED: 'Comments on tickets I follow',
  STATUS: 'Status changes',
  UPDATED: 'Priority, due date, sprint and release changes',
  COMMIT: 'Commits linked to my tickets',
  DUE: 'Daily due-date reminders',
  CUSTOM: 'Automation messages',
};

/** "2026-10-07T09:15:00.123456" -> "now", "5m", "2h", "3d", "7 Oct". Server times are local (IST). */
export function relTime(v) {
  if (!v) return '';
  const t = new Date(String(v).slice(0, 23)).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d`;
  return new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function Row({ n, onOpen }) {
  const unread = !n.readAt;
  return (
    <ButtonBase onClick={() => onOpen(n)} focusRipple
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, width: '100%', textAlign: 'left',
                      px: 2, py: 1.25, bgcolor: unread ? '#f2f7fd' : 'transparent',
                      borderBottom: '1px solid #eef1f5', '&:hover': { bgcolor: unread ? '#e8f1fb' : '#f7f9fb' } }}>
      {n.actor ? (
        <Avatar sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 700, bgcolor: colorFor(n.actor.id), mt: 0.25 }}>
          {n.actor.initials}
        </Avatar>
      ) : (
        <Avatar sx={{ width: 30, height: 30, bgcolor: '#eef1f5', color: '#5f6b7a', mt: 0.25 }}>
          <SmartToyOutlined sx={{ fontSize: 18 }} />
        </Avatar>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: unread ? 650 : 500, lineHeight: 1.35 }}>{n.title}</Typography>
        {n.body && (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.4, mt: 0.25,
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {n.body}
          </Typography>
        )}
      </Box>
      <Stack sx={{ alignItems: 'flex-end', gap: 0.75, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{relTime(n.createdAt)}</Typography>
        {unread && <Box aria-label="Unread" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#009da1' }} />}
      </Stack>
    </ButtonBase>
  );
}

function PrefsDialog({ open, onClose }) {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPrefs(null);
    workNotifications.prefs().then(setPrefs).catch((e) => { notify.error(e.message); onClose(); });
  }, [open, onClose]);

  const types = (prefs && prefs.types) || Object.keys(TYPE_LABELS);
  const muted = new Set((prefs && prefs.mutedTypes) || []);

  function toggleMuted(t) {
    const next = new Set(muted);
    if (next.has(t)) next.delete(t); else next.add(t);
    setPrefs({ ...prefs, mutedTypes: [...next] });
  }

  async function save() {
    setBusy(true);
    try {
      await workNotifications.savePrefs({
        emailEnabled: prefs.emailEnabled, pushEnabled: prefs.pushEnabled, mutedTypes: prefs.mutedTypes,
      });
      notify.success('Notification settings saved');
      onClose();
    } catch (e) {
      notify.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs"
            slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      <DialogTitle sx={{ fontSize: 17, fontWeight: 700 }}>Notification settings</DialogTitle>
      <DialogContent>
        {!prefs ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : (
          <>
            <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', mb: 0.5 }}>
              Channels
            </Typography>
            <FormControlLabel
              control={<Switch checked={!!prefs.emailEnabled}
                               onChange={(e) => setPrefs({ ...prefs, emailEnabled: e.target.checked })} />}
              label={<Typography sx={{ fontSize: 14 }}>Email me when I'm assigned or @mentioned</Typography>} />
            <FormControlLabel
              control={<Switch checked={!!prefs.pushEnabled}
                               onChange={(e) => setPrefs({ ...prefs, pushEnabled: e.target.checked })} />}
              label={<Typography sx={{ fontSize: 14 }}>Push to my phone</Typography>} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', mt: 2 }}>
              Notify me about
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 0.5 }}>
              Untick anything you don't want at all - not even in the bell.
            </Typography>
            <Stack>
              {types.map((t) => (
                <FormControlLabel key={t} sx={{ my: -0.25 }}
                  control={<Checkbox size="small" checked={!muted.has(t)} onChange={() => toggleMuted(t)} />}
                  label={<Typography sx={{ fontSize: 13.5 }}>{TYPE_LABELS[t] || t}</Typography>} />
              ))}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!prefs || busy}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Bell for the top app bar: unread badge (polled every 45s while the tab is
 * visible, and on focus), and a popover listing recent notifications.
 * Clicking one marks it read and opens its ticket via onOpenTicket(key), or
 * the pay page via onOpenPay() for a payslip.
 */
export default function NotificationBell({ onOpenTicket, onOpenPay }) {
  const [unread, setUnread] = useState(0);
  const [anchor, setAnchor] = useState(null);
  const [tab, setTab] = useState('unread');
  const [items, setItems] = useState(null);
  const [prefsOpen, setPrefsOpen] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      const r = await workNotifications.unreadCount();
      setUnread(Number(r && r.unread) || 0);
    } catch {
      // A background poll failing (offline, signed out) shouldn't toast every 45s.
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const tick = () => { if (document.visibilityState === 'visible') refreshCount(); };
    const id = setInterval(tick, POLL_MS);
    window.addEventListener('focus', refreshCount);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', refreshCount);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [refreshCount]);

  const loadList = useCallback(async (which) => {
    setItems(null);
    try {
      const r = await workNotifications.list({ unreadOnly: which === 'unread', limit: 30 });
      setItems(r.items || []);
      setUnread(Number(r.unread) || 0);
    } catch (e) {
      notify.error(e.message);
      setItems([]);
    }
  }, []);

  useEffect(() => {
    if (anchor) loadList(tab);
  }, [anchor, tab, loadList]);

  async function markAll() {
    try {
      await workNotifications.markAllRead();
      setUnread(0);
      setItems((cur) => (tab === 'unread' ? [] : (cur || []).map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() }))));
    } catch (e) {
      notify.error(e.message);
    }
  }

  function open(n) {
    if (!n.readAt) {
      setUnread((u) => Math.max(0, u - 1));
      setItems((cur) => (cur || []).map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)));
      workNotifications.markRead([n.id]).catch(() => { /* the next poll corrects the count */ });
    }
    setAnchor(null);
    if (n.type === 'PAYSLIP') {
      if (onOpenPay) onOpenPay();
    } else if (n.itemKey) {
      if (onOpenTicket) onOpenTicket(n.itemKey);
    } else {
      notify.info('That ticket no longer exists');
    }
  }

  const closePrefs = useCallback(() => setPrefsOpen(false), []);

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton onClick={(e) => setAnchor(e.currentTarget)}
                    aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
                    aria-haspopup="dialog" sx={{ flexShrink: 0 }}>
          <Badge badgeContent={unread} max={99} color="error"
                 sx={{ '& .MuiBadge-badge': { fontSize: 10.5, height: 18, minWidth: 18, fontWeight: 700 } }}>
            {unread ? <NotificationsRounded sx={{ color: '#123871' }} /> : <NotificationsNoneRounded />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}
               anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
               transformOrigin={{ vertical: 'top', horizontal: 'right' }}
               slotProps={{ paper: { sx: { mt: 1, width: 380, maxWidth: 'calc(100vw - 16px)', maxHeight: 520,
                                           borderRadius: 3, display: 'flex', flexDirection: 'column' } } }}>
        <Stack direction="row" sx={{ alignItems: 'center', px: 2, pt: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, flex: 1 }}>Notifications</Typography>
          <Button size="small" startIcon={<DoneAllRounded />} onClick={markAll} disabled={!unread}
                  sx={{ textTransform: 'none' }}>
            Mark all read
          </Button>
        </Stack>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 1, minHeight: 36, borderBottom: '1px solid #eef1f5',
                                                               '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontSize: 13 } }}>
          <Tab value="unread" label={unread ? `Unread (${unread > 99 ? '99+' : unread})` : 'Unread'} />
          <Tab value="all" label="All" />
        </Tabs>

        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 120 }}>
          {items === null ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 5 }}><CircularProgress size={24} /></Box>
          ) : items.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5, px: 3 }}>
              <NotificationsNoneRounded sx={{ fontSize: 36, color: '#c4ccd6' }} />
              <Typography sx={{ fontWeight: 650, fontSize: 14, mt: 0.5 }}>You're all caught up</Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
                {tab === 'unread'
                  ? 'New assignments, @mentions and comments on tickets you watch will show up here.'
                  : 'Nothing yet - watch a ticket to hear about its comments and status changes.'}
              </Typography>
            </Box>
          ) : (
            items.map((n) => <Row key={n.id} n={n} onOpen={open} />)
          )}
        </Box>

        <Divider />
        <Stack direction="row" sx={{ alignItems: 'center', px: 1, py: 0.5 }}>
          <Tooltip title="Notification settings">
            <IconButton size="small" aria-label="Notification settings"
                        onClick={() => { setAnchor(null); setPrefsOpen(true); }}>
              <SettingsOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Email, push and what to hear about</Typography>
        </Stack>
      </Popover>

      <PrefsDialog open={prefsOpen} onClose={closePrefs} />
    </>
  );
}
